import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../lib/auth-context';
import { HeaderSearch } from './header-search';
import { type AdminNavItem, isNavSectionActive, visibleNavItems } from './nav-items';

export function Layout({ children }: { children: ReactNode }) {
  const { user, signOut, can } = useAuth();
  const navItems: AdminNavItem[] = visibleNavItems(can);
  const location = useLocation();

  return (
    <div className="flex h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col border-r border-slate-700 bg-slate-800">
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="text-lg font-bold text-white">Pop</span>
          <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-slate-900">
            Admin
          </span>
        </div>

        <nav className="mt-2 flex flex-1 flex-col gap-0.5 px-3">
          {navItems.map((item) => {
            const children = item.children;
            if (!children) {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2 text-sm font-medium transition ${
                      isActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            }

            const isSectionActive = isNavSectionActive(item, location.pathname);

            return (
              <div key={item.to} className="space-y-1">
                <NavLink
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition ${
                      isActive || isSectionActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition ${isSectionActive ? 'text-slate-300' : '-rotate-90 text-slate-500'}`}
                  />
                </NavLink>

                {isSectionActive ? (
                  <div className="ml-3 space-y-1 border-l border-slate-700/80 pl-3">
                    {children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.to === '/analytics'}
                        className={({ isActive }) =>
                          `block rounded-lg px-3 py-2 text-sm transition ${
                            isActive
                              ? 'bg-slate-700/80 text-white'
                              : 'text-slate-400 hover:bg-slate-700/40 hover:text-slate-200'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between border-b border-slate-700 px-6 py-3">
          <div>
            <HeaderSearch />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">{user?.email}</span>
            <button
              type="button"
              onClick={signOut}
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-300 transition hover:bg-slate-700"
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}
